
import React, { useState, useEffect } from 'react';
import { supabase, logoutUser } from '../supabaseClient';
import { UserProfile } from '../types';
import { Avatar } from './Avatar';
import { getTier, getTierDetails, getNextTierXP, getPrevTierXP, awardOnce, XP_VALUES } from '../services/gamificationService';
import { YOUTUBE_TRACKS } from '../ytTracks';
import {
  User,
  Smartphone,
  Save,
  Loader2,
  Play,
  Camera,
  X,
  Rocket,
  Shield,
  Trophy,
  Volume2,
  Star,
  Zap,
  Crown,
  UserPlus,
  Trophy as RankingIcon,
  Instagram,
  Youtube,
  Globe,
  Linkedin,
  Twitter,
  Facebook,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Sparkles,
  BookOpen,
  Car,
  LogOut
} from 'lucide-react';

const VIP_EMAILS = [
  'tiagoromao25@gmail.com',
  'anapaulaautentica@gmail.com',
  'eenokibara@gmail.com',
  'josesantostimes@gmail.com',
  'ana.ubersp@gmail.com',
  'nelsandromatias@hotmail.com',
  'mmmontefuscobernardo@gmail.com',
  'mmontefuscobernardo@gmail.com'
];

const isVipEmail = (email: string | undefined): boolean => {
  if (!email) return false;
  const lowerEmail = email.toLowerCase();
  if (lowerEmail.startsWith('eenokibara@')) return true;
  return VIP_EMAILS.includes(lowerEmail);
};
import { SubscriptionModal } from './SubscriptionModal';
import { ShareApp } from './ShareApp';

interface Props {
  userProfile: UserProfile | null;
  currentXP: number;
  onProfileUpdate: () => void;
  viewingProfileId?: string | null;
  onRestartTour?: () => void;
  onOpenAIGenerator?: () => void;
  theme?: 'dark' | 'light';
  onThemeChange?: (theme: 'dark' | 'light') => void;
}


export const ProfileView: React.FC<Props> = ({ 
  userProfile, 
  currentXP, 
  onProfileUpdate, 
  viewingProfileId, 
  onRestartTour, 
  onOpenAIGenerator,
  theme = 'dark',
  onThemeChange
}) => {
  const [nickname, setNickname] = useState('');
  const [phone, setPhone] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [alarmSound, setAlarmSound] = useState('keep_calm');
  const [isAlarmEnabled, setIsAlarmEnabled] = useState(false); // New State
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper to normalize social links
  const normalizeSocialLink = (input: string, platform: 'ig' | 'yt' | 'tt' | 'in' | 'x' | 'fb') => {
    if (!input) return '';
    let clean = input.trim().replace(/\/$/, ""); 
    if (clean.startsWith('http')) return clean;
    clean = clean.replace(/^@/, '');
    
    switch(platform) {
      case 'ig': return `https://instagram.com/${clean}`;
      case 'yt': return `https://youtube.com/${clean}`;
      case 'tt': return `https://tiktok.com/@${clean}`;
      case 'in': return `https://linkedin.com/in/${clean}`;
      case 'x': return `https://x.com/${clean}`;
      case 'fb': return `https://facebook.com/${clean}`;
      default: return clean;
    }
  };

  const [isSaving, setIsSaving] = useState(false);
  const [playingYtId, setPlayingYtId] = useState<string | null>(null);
  const [displayProfile, setDisplayProfile] = useState<UserProfile | null>(null);
  const [isOtherProfileLoading, setIsOtherProfileLoading] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [referralCount, setReferralCount] = useState(0);
  const [instagramUser, setInstagramUser] = useState('');
  const [youtubeChannel, setYoutubeChannel] = useState('');
  const [tiktokUser, setTiktokUser] = useState('');
  const [linkedinUser, setLinkedinUser] = useState('');
  const [xUser, setXUser] = useState('');
  const [facebookUser, setFacebookUser] = useState('');
  const [showSocials, setShowSocials] = useState(false);
  const [privacyHideStats, setPrivacyHideStats] = useState(false);
  const [privacyHideAvatar, setPrivacyHideAvatar] = useState(false);
  const [tourEnabled, setTourEnabled] = useState(() => localStorage.getItem('driver_score_tour_enabled') !== 'false');
  const [garageTourEnabled, setGarageTourEnabled] = useState(() => localStorage.getItem('driver_score_garage_tour_enabled') !== 'false');
  const [generation, setGeneration] = useState<'OLD' | 'NEW'>('OLD');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Gamification Logic Calculation (Moved up for tier check)
  const tier = getTier(currentXP);
  const details = getTierDetails(tier);

  useEffect(() => {
    // Load Alarm Toggle Preference
    const savedAlarm = localStorage.getItem('driver_score_alarm_enabled');
    setIsAlarmEnabled(savedAlarm === 'true');
    const savedSound = localStorage.getItem('driver_score_alarm_sound');
    if (savedSound) setAlarmSound(savedSound);

    const loadProfile = async () => {
      const userIdToFetch = viewingProfileId || userProfile?.id;
      if (viewingProfileId && viewingProfileId !== userProfile?.id) {
        setIsOtherProfileLoading(true);
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', viewingProfileId)
            .maybeSingle(); // Refined to avoid 406 Not Acceptable errors
          if (error) throw error;
          setDisplayProfile(data);
          setNickname(data.nickname || '');
          setPhone(data.phone || '');
          setAvatarUrl(data.avatar_url || '');
          if (data.alarm_sound) setAlarmSound(data.alarm_sound);
          setXUser(data.x_user || '');
          setFacebookUser(data.facebook_user || '');
          setShowSocials(data.show_socials || false);
          setPrivacyHideStats(data.privacy_hide_stats || false);
          setPrivacyHideAvatar(data.privacy_hide_avatar || false);
          setGeneration(data.generation || 'OLD');
        } catch (err) {
          console.error("Error fetching other profile:", err);
          setDisplayProfile(null);
        } finally {
          setIsOtherProfileLoading(false);
        }
      } else {
        setDisplayProfile(userProfile);
        if (userProfile) {
          setNickname(userProfile.nickname || '');
          setPhone(userProfile.phone || '');
          setAvatarUrl(userProfile.avatar_url || '');
          if (userProfile.alarm_sound) {
            setAlarmSound(userProfile.alarm_sound);
          } else {
            if (tier === 'OURO' || details.categoria !== 'Base') {
              setAlarmSound('keep_calm');
            }
          }
          setInstagramUser(userProfile.instagram_user || '');
          setYoutubeChannel(userProfile.youtube_channel || '');
          setTiktokUser(userProfile.tiktok_user || '');
          setLinkedinUser(userProfile.linkedin_user || '');
          setXUser(userProfile.x_user || '');
          setFacebookUser(userProfile.facebook_user || '');
          setShowSocials(userProfile.show_socials || false);
          setPrivacyHideStats(userProfile.privacy_hide_stats || false);
          setPrivacyHideAvatar(userProfile.privacy_hide_avatar || false);
          setGeneration(userProfile.generation || 'OLD');
        }
      }
      
      // Fetch referrals
      if (userIdToFetch) {
        const { count } = await supabase
          .from('referrals')
          .select('id', { count: 'exact', head: true })
          .eq('referrer_id', userIdToFetch);
        if (count !== null) setReferralCount(count);
      }
    };

    loadProfile();
  }, [userProfile, viewingProfileId, tier]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userProfile?.id) return;

    setIsSaving(true);
    try {
      // Use upsert with onConflict to handle cases where profile row doesn't exist yet 
      // (common for Google-login and VIP users whose row wasn't auto-created)
      const coreUpdates = {
        id: userProfile.id,
        nickname,
        avatar_url: avatarUrl,
      };

      const { error: coreError } = await supabase
        .from('profiles')
        .upsert(coreUpdates, { onConflict: 'id' });
      if (coreError) throw coreError;

      // Step 2: Try saving phone (may fail if column doesn't exist in some DB versions)
      if (phone !== undefined && phone !== null) {
        const { error: phoneError } = await supabase
          .from('profiles')
          .update({ phone })
          .eq('id', userProfile.id);
        if (phoneError) console.warn('Could not save phone (column may be missing):', phoneError.message);
      }

      // Step 3: Try saving social media and privacy fields
      const settingsUpdates: Record<string, any> = {};
      settingsUpdates.instagram_user = instagramUser || null;
      settingsUpdates.youtube_channel = youtubeChannel || null;
      settingsUpdates.tiktok_user = tiktokUser || null;
      settingsUpdates.linkedin_user = linkedinUser || null;
      settingsUpdates.x_user = xUser || null;
      settingsUpdates.facebook_user = facebookUser || null;
      settingsUpdates.show_socials = showSocials;
      settingsUpdates.privacy_hide_stats = privacyHideStats;
      settingsUpdates.privacy_hide_avatar = privacyHideAvatar;
      settingsUpdates.generation = generation;
      
      const { error: settingsError } = await supabase
        .from('profiles')
        .update(settingsUpdates)
        .eq('id', userProfile.id);
      if (settingsError) console.warn('Could not save settings fields (columns may be missing):', settingsError.message);

      // Step 3: Try saving alarm_sound
      if (alarmSound) {
        const { error: soundError } = await supabase
          .from('profiles')
          .update({ alarm_sound: alarmSound })
          .eq('id', userProfile.id);
        if (soundError) console.warn('Could not save alarm_sound (column may be missing):', soundError.message);
      }

      onProfileUpdate();

      // Award one-time XP for profile completion actions
      if (userProfile?.id) {
        const uid = userProfile.id;
        if (avatarUrl && avatarUrl !== userProfile.avatar_url) await awardOnce(uid, 'profile_photo', XP_VALUES.PROFILE_PHOTO);
        if (phone && phone !== userProfile.phone)           await awardOnce(uid, 'profile_phone', XP_VALUES.PROFILE_PHONE);
        if (nickname && nickname !== userProfile.nickname)  await awardOnce(uid, 'profile_nickname', XP_VALUES.PROFILE_NICKNAME);
      }

      alert('Perfil atualizado com sucesso! ❤️');
    } catch (error: any) {
      console.error('Error updating profile:', error);
      alert(`Erro ao salvar perfil: ${error.message || 'Tente novamente.'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!userProfile?.id || !userProfile.subscription_id) return;
    
    if (!confirm('Tem certeza que deseja cancelar sua assinatura? Você ainda terá acesso Premium até o fim do período já pago, mas a renovação automática será desativada.')) {
      return;
    }

    setIsSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: {
          userId: userProfile.id,
          subscriptionId: userProfile.subscription_id
        }
      });

      if (error) throw error;
      
      alert('Sua assinatura foi cancelada com sucesso. A renovação automática está desativada.');
      onProfileUpdate(); // Refresh the profile from DB (webhook might have unset subscription_id)
    } catch (err: any) {
      console.error('Error cancelling subscription:', err);
      alert(`Erro ao cancelar assinatura: ${err.message || 'Tente novamente mais tarde.'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleAlarm = async () => {
    const newState = !isAlarmEnabled;
    setIsAlarmEnabled(newState);
    localStorage.setItem('driver_score_alarm_enabled', String(newState));

    // Award XP for enabling alarm for the first time
    if (newState && userProfile?.id) {
      await awardOnce(userProfile.id, 'alarm_enabled', XP_VALUES.ALARM_ENABLED);
    }

    if (newState) {
      if ('Notification' in window) {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          try {
            if ('serviceWorker' in navigator) {
              const registration = await navigator.serviceWorker.ready;
              registration.showNotification("Despertador Driver Score", {
                body: "Notificações de alarme ativadas com sucesso! ⏰",
                icon: "/logo192.png",
                badge: "/logo192.png"
              });
            } else {
              new Notification("Despertador Driver Score", {
                body: "Notificações de alarme ativadas com sucesso! ⏰",
                icon: "/logo192.png"
              });
            }
          } catch (e) {
            console.error("Failed to show test notification:", e);
          }
        } else {
          alert("Por favor, ative as permissões de notificação no seu navegador para que o despertador possa te notificar.");
        }
      }
    }
  };

  // Image Compression Helper
  const compressImage = (file: File): Promise<Blob> => {
    return new Promise((resolve) => {
      const reader = new window.FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new window.Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 400; // Tamanho ideal para avatar
          const scaleSize = MAX_WIDTH / img.width;
          canvas.width = MAX_WIDTH;
          canvas.height = img.height * scaleSize;

          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            // Converte para JPEG com 70% de qualidade
            canvas.toBlob((blob) => {
              if (blob) resolve(blob);
            }, 'image/jpeg', 0.7);
          }
        };
      };
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userProfile?.id) return;

    setIsSaving(true);
    try {
      // 1. Compress
      const compressedBlob = await compressImage(file);

      // 2. Upload to Storage
      const fileName = `${userProfile.id}/${Date.now()}.jpg`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, compressedBlob, { upsert: true });

      if (uploadError) {
        console.error("Storage upload error:", uploadError);
        throw new Error("Erro no upload: Verifique se o bucket 'avatars' existe e é público.");
      }

      // 3. Get Public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      // 4. Update Profile in Database
      // Using .update() instead of .upsert() for clarity since profile already exists
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ 
          avatar_url: publicUrl
        })
        .eq('id', userProfile.id);

      if (profileError) {
        console.error("Database profile update error:", profileError);
        // We set the state anyway so user sees the change immediately, 
        // but we warn that it might not be permanent.
        setAvatarUrl(publicUrl);
        throw new Error("Foto enviada, mas houve um erro ao salvar no seu perfil. Tente novamente.");
      }

      // 5. Finalize Success
      setAvatarUrl(publicUrl);
      onProfileUpdate();
      alert("Foto atualizada com sucesso!");

    } catch (error: any) {
      console.error("ProfileView - handleFileSelect error:", error);
      alert(error.message || "Erro inesperado ao processar a imagem.");
    } finally {
      setIsSaving(false);
    }
  };

  const stopAlarm = () => {
    setPlayingYtId(null);
  };

  const nextLevelXP = getNextTierXP(currentXP);

  // Calculate relative progress for better UX
  const prevLevelXP = getPrevTierXP(currentXP);

  const range = nextLevelXP - prevLevelXP;
  const progressInRange = currentXP - prevLevelXP;
  const progressPercent = Math.min(100, Math.max(0, (progressInRange / range) * 100));

  const isGoldOrPlatina = tier === 'OURO' || details.categoria !== 'Base';
  const isOwnProfile = !viewingProfileId || viewingProfileId === userProfile?.id;
  
  // Subscription expiration check
  const isSubscriptionExpired = (() => {
    if (!displayProfile?.is_premium) return false;
    if (!displayProfile.subscription_end_date) return false; // No end date = auto-renewal active
    return new Date(displayProfile.subscription_end_date) <= new Date();
  })();
  const isPremiumOrVip = (displayProfile?.is_premium && !isSubscriptionExpired) || isVipEmail(displayProfile?.phone + "@driverscore.com");
  const isAdmin = userProfile?.email === 'tiagoromao25@gmail.com' || isVipEmail(userProfile?.email);
  const isViewingOtherAsAdmin = !isOwnProfile && isAdmin && userProfile?.email === 'tiagoromao25@gmail.com';

  if (isOtherProfileLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-500">
        <Loader2 className="w-8 h-8 animate-spin mb-2 text-orange-500" />
        <p>Carregando perfil...</p>
      </div>
    );
  }

  const profileToDisplay = displayProfile || userProfile;
  const isTargetHidingStats = !isOwnProfile && profileToDisplay?.privacy_hide_stats;
  const isTargetHidingAvatar = !isOwnProfile && profileToDisplay?.privacy_hide_avatar;

  const xpToDisplay = isTargetHidingStats ? 0 : (profileToDisplay?.xp_points || profileToDisplay?.xp || 0);
  const displayTier = getTier(xpToDisplay);
  const displayDetails = getTierDetails(displayTier);
  
  const displayPrevXP = getPrevTierXP(xpToDisplay);
  const displayNextXP = getNextTierXP(xpToDisplay);
  const displayRange = displayNextXP - displayPrevXP;
  const displayProgressPercent = displayRange > 0 
    ? Math.min(100, Math.max(0, ((xpToDisplay - displayPrevXP) / displayRange) * 100))
    : 100;

  return (
    <div className="max-w-5xl mx-auto px-4 space-y-6 animate-fade-in pb-20">

      {/* 1. Profile Header / Card (LinkedIn Style Cover) */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-2xl shadow-2xl relative overflow-hidden">
        {/* Banner de Capa */}
        <div className={`h-32 sm:h-44 bg-gradient-to-r ${displayDetails.gradient} relative overflow-hidden`}>
          {/* Abstract overlay */}
          <div className="absolute inset-0 bg-black/10 mix-blend-overlay"></div>
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:18px_18px] opacity-20"></div>
          <div className="absolute -left-10 -top-10 w-44 h-44 bg-white/10 rounded-full blur-3xl"></div>
          
          {/* Crown badge for premium in top right */}
          {profileToDisplay?.is_premium && (
            <div className="absolute top-3 right-3 bg-slate-900/80 backdrop-blur-md border border-amber-500/30 text-amber-400 text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-lg">
              <Crown className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
              {profileToDisplay.is_founder ? 'Fundador' : 'Elite'}
            </div>
          )}
        </div>

        {/* Header Main Body */}
        <div className="p-6 pt-0">
          <div className="flex flex-col md:flex-row items-center md:items-end gap-5 -mt-16 md:-mt-20 mb-6 relative z-10 text-center md:text-left">
            <div className="relative group">
              <Avatar 
                xp={xpToDisplay} 
                url={isTargetHidingAvatar ? '' : avatarUrl} 
                size="xl" 
                showBadge={!isTargetHidingStats} 
                showLabel={!isTargetHidingStats}
                isPremium={profileToDisplay?.is_premium}
                isVip={isVipEmail(profileToDisplay?.email)}
                disablePulsing={true}
              />
              {isOwnProfile && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-1 right-1 bg-slate-800 text-white p-2 rounded-full border border-slate-600 shadow-lg hover:bg-slate-700 transition-colors"
                >
                  <Camera className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h2 className="text-2xl sm:text-3xl font-black text-white flex items-center justify-center md:justify-start gap-2">
                {nickname || 'Motorista'}
                {profileToDisplay?.is_premium && !isSubscriptionExpired && (
                  <div className="bg-amber-500/10 border border-amber-500/50 p-1 rounded-full animate-pulse-gentle" title="Membro Premium">
                    <Crown className="w-4 h-4 text-amber-500 fill-amber-500" />
                  </div>
                )}
                {(isOwnProfile || isAdmin) && profileToDisplay?.is_founder && isSubscriptionExpired && (
                  <div className="bg-slate-600/10 border border-slate-500/50 p-1 rounded-full" title="Fundador (Assinatura Expirada)">
                    <Crown className="w-4 h-4 text-slate-500 fill-slate-500" />
                  </div>
                )}
              </h2>
              <p className={`text-sm font-semibold uppercase tracking-wider mt-1.5 ${isTargetHidingStats ? 'text-slate-500' : displayDetails.color} flex items-center justify-center md:justify-start gap-1.5`}>
                {isTargetHidingStats ? <EyeOff className="w-4 h-4" /> : <span className="text-base leading-none">{displayDetails.icon}</span>} 
                {isTargetHidingStats ? 'Piloto Oculto' : displayDetails.label}
              </p>

              {/* Subtitle Contact Information */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-4 gap-y-1.5 mt-3 text-xs text-slate-400">
                {phone && (
                  <span className="flex items-center gap-1.5">
                    <Smartphone className="w-3.5 h-3.5 text-slate-500" />
                    {phone}
                  </span>
                )}
                {profileToDisplay?.email && (isOwnProfile || isAdmin) && (
                  <span className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-slate-500" />
                    {profileToDisplay.email}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* XP Progress Bar Section */}
          {!isTargetHidingStats ? (
            <div className="w-full bg-slate-900/30 border border-slate-700/30 p-4 rounded-xl mt-4">
              <div className="flex justify-between text-xs text-slate-400 mb-1.5 font-semibold">
                <span>{xpToDisplay} XP Acumulados</span>
                {displayTier !== 'SINGULARIDADE' ? (
                  <span>Próximo Elo: {getNextTierXP(xpToDisplay)} XP</span>
                ) : (
                  <span className="text-cyan-400 font-bold flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5" /> Lenda Viva!
                  </span>
                )}
              </div>
              <div className="w-full h-3 bg-slate-800/80 rounded-full overflow-hidden border border-slate-700 relative">
                <div
                  className={`h-full bg-gradient-to-r ${displayDetails.gradient} transition-all duration-1000 ease-out`}
                  style={{ width: `${displayTier === 'SINGULARIDADE' ? 100 : displayProgressPercent}%` }}
                ></div>
              </div>
              <p className="text-center text-xs text-slate-500 mt-2">
                {tier === 'SINGULARIDADE'
                  ? 'Você atingiu o nível máximo!'
                  : `Faltam ${getNextTierXP(xpToDisplay) - xpToDisplay} XP para o próximo elo.`}
              </p>
            </div>
          ) : (
            <div className="w-full mt-4 p-4 bg-slate-900/50 rounded-xl border border-slate-800 text-center">
              <p className="text-xs text-slate-500 italic">Estatísticas ocultas pelo motorista.</p>
            </div>
          )}

          {/* Social Media Links inside header */}
          {profileToDisplay?.show_socials && (
            <div className="w-full border-t border-slate-800/50 pt-4 mt-6">
              <p className="text-[10px] text-amber-500 font-extrabold uppercase tracking-wider mb-3 flex items-center gap-1.5 justify-center md:justify-start">
                <Eye className="w-3.5 h-3.5 text-amber-500" /> Redes Sociais Públicas
              </p>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                {profileToDisplay.instagram_user && (
                  <a href={normalizeSocialLink(profileToDisplay.instagram_user, 'ig')} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-900/60 rounded-lg border border-slate-700 hover:border-amber-500/50 hover:bg-amber-500/10 transition-all group" title="Instagram">
                    <Instagram className="w-4 h-4 text-amber-500 group-hover:scale-110 transition-transform" />
                  </a>
                )}
                {profileToDisplay.youtube_channel && (
                  <a href={normalizeSocialLink(profileToDisplay.youtube_channel, 'yt')} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-900/60 rounded-lg border border-slate-700 hover:border-amber-500/50 hover:bg-amber-500/10 transition-all group" title="YouTube">
                    <Youtube className="w-4 h-4 text-amber-500 group-hover:scale-110 transition-transform" />
                  </a>
                )}
                {profileToDisplay.tiktok_user && (
                  <a href={normalizeSocialLink(profileToDisplay.tiktok_user, 'tt')} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-900/60 rounded-lg border border-slate-700 hover:border-amber-500/50 hover:bg-amber-500/10 transition-all group" title="TikTok">
                    <Globe className="w-4 h-4 text-amber-500 group-hover:scale-110 transition-transform" />
                  </a>
                )}
                {profileToDisplay.linkedin_user && (
                  <a href={normalizeSocialLink(profileToDisplay.linkedin_user, 'in')} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-900/60 rounded-lg border border-slate-700 hover:border-amber-500/50 hover:bg-amber-500/10 transition-all group" title="LinkedIn">
                    <Linkedin className="w-4 h-4 text-amber-500 group-hover:scale-110 transition-transform" />
                  </a>
                )}
                {profileToDisplay.x_user && (
                  <a href={normalizeSocialLink(profileToDisplay.x_user, 'x')} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-900/60 rounded-lg border border-slate-700 hover:border-amber-500/50 hover:bg-amber-500/10 transition-all group" title="X (Twitter)">
                    <Twitter className="w-4 h-4 text-amber-500 group-hover:scale-110 transition-transform" />
                  </a>
                )}
                {profileToDisplay.facebook_user && (
                  <a href={normalizeSocialLink(profileToDisplay.facebook_user, 'fb')} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-900/60 rounded-lg border border-slate-700 hover:border-amber-500/50 hover:bg-amber-500/10 transition-all group" title="Facebook">
                    <Facebook className="w-4 h-4 text-amber-500 group-hover:scale-110 transition-transform" />
                  </a>
                )}
              </div>
            </div>
          )}
          {!profileToDisplay?.show_socials && !isOwnProfile && (
            <p className="text-[10px] text-slate-600 text-center md:text-left mt-4 italic">Redes sociais privadas</p>
          )}
        </div>
      </div>

      {/* 2. Grid Columns for Edit Form & Sidebar Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        
        {/* Left Column: Form & Preferences */}
        {isOwnProfile && (
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-400" /> Configurações & Identidade
              </h3>

              <form onSubmit={handleSave} className="space-y-5">
                {/* Photo Input (Hidden) */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/*"
                  className="hidden"
                />

                {/* Avatar "Field" (Visual only, triggers input) */}
                <div>
                  <label htmlFor="avatar_upload" className="block text-sm font-medium text-slate-400 mb-2">Foto de Perfil</label>
                  <button
                    id="avatar_upload"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full bg-slate-900 border border-slate-600 border-dashed rounded-lg p-4 text-slate-400 text-sm hover:bg-slate-800/50 hover:border-slate-500 hover:text-slate-300 transition-all flex items-center justify-center gap-2"
                  >
                    <Camera className="w-4 h-4" />
                    {avatarUrl ? 'Trocar Foto' : 'Enviar Foto'}
                  </button>
                  {avatarUrl && <p className="text-[10px] text-slate-500 mt-1 truncate">Atual: {avatarUrl}</p>}
                </div>

                <div>
                  <label htmlFor="nickname" className="block text-sm font-medium text-slate-400 mb-2">Apelido (Nickname)</label>
                  <div className="relative">
                    <User className="absolute left-3 top-3.5 w-5 h-5 text-slate-500" />
                    <input
                      id="nickname"
                      name="nickname"
                      type="text"
                      value={nickname}
                      onChange={(e) => setNickname(e.target.value)}
                      readOnly={!isOwnProfile}
                      placeholder="Como quer ser chamado?"
                      className={`w-full bg-slate-900 border border-slate-600 rounded-lg pl-10 pr-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none ${!isOwnProfile ? 'opacity-70 cursor-not-allowed' : ''}`}
                    />
                  </div>
                </div>

                {/* Alarm Config */}
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Volume2 className="w-4 h-4 text-orange-500" />
                      <label htmlFor="alarm_enabled" className="text-sm font-medium text-slate-300">Despertador Automático</label>
                    </div>

                    {/* Custom Toggle Switch */}
                    <button
                      id="alarm_enabled"
                      type="button"
                      onClick={handleToggleAlarm}
                      className={`w-12 h-6 rounded-full p-1 transition-colors relative ${isAlarmEnabled ? (isGoldOrPlatina ? 'bg-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.5)]' : 'bg-green-500') : 'bg-slate-700'}`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${isAlarmEnabled ? 'translate-x-6' : 'translate-x-0'}`} />
                    </button>
                  </div>

                  {isAlarmEnabled && (
                    <div className="space-y-2 mt-4 animate-fade-in max-h-64 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
                      <p className="text-xs text-slate-500 mb-4 uppercase tracking-wide flex justify-between items-center">
                        <span>Músicas de Despertador (YouTube)</span>
                        <span className="text-[10px] bg-slate-800 px-2 py-0.5 rounded-full border border-slate-700">54 músicas disponíveis - Projeto ROMANOSV3</span>
                      </p>

                      {/* Aleatório option */}
                      <div
                        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all mb-3 ${
                          alarmSound === 'random'
                            ? 'border-orange-500 bg-orange-500/10'
                            : 'border-slate-700 bg-slate-800 hover:border-slate-500'
                        }`}
                        onClick={() => { setAlarmSound('random'); localStorage.setItem('driver_score_alarm_sound', 'random'); }}
                      >
                        <span className="text-2xl">🎲</span>
                        <div>
                          <p className="text-sm font-bold text-white">Aleatório</p>
                          <p className="text-xs text-slate-400">Uma música diferente a cada despertar</p>
                        </div>
                        {alarmSound === 'random' && <span className="ml-auto text-orange-400 text-xs font-bold">✓</span>}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {YOUTUBE_TRACKS.map(track => (
                          <div
                            key={track.id}
                            className={`relative flex flex-col bg-slate-800 rounded-xl overflow-hidden border cursor-pointer transition-all ${alarmSound === track.id ? 'border-orange-500 shadow-[0_0_15px_rgba(249,115,22,0.3)]' : 'border-slate-700 hover:border-slate-500'}`}
                            onClick={async () => { 
                              setAlarmSound(track.id); 
                              localStorage.setItem('driver_score_alarm_sound', track.id);
                              if (userProfile?.id) {
                                await awardOnce(userProfile.id, 'alarm_sound_selected', XP_VALUES.ALARM_SOUND_SELECTED);
                              }
                            }}
                          >
                            {/* Lazy Loaded Thumbnail */}
                            <div className="aspect-video bg-slate-900 relative group overflow-hidden" onClick={(e) => { e.stopPropagation(); setPlayingYtId(track.ytId); }}>
                              <img
                                src={`https://img.youtube.com/vi/${track.ytId}/mqdefault.jpg`}
                                className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-all duration-500 group-hover:scale-105"
                                alt={track.label}
                                onError={(e) => (e.currentTarget.style.display = 'none')}
                              />
                              {/* Play Overlay */}
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-12 h-12 bg-black/60 rounded-full flex items-center justify-center group-hover:bg-red-600 transition-colors backdrop-blur-sm shadow-xl">
                                  <Play className="w-6 h-6 text-white fill-white ml-1" />
                                </div>
                              </div>
                            </div>

                            {/* Info & Radio */}
                            <div className="p-3 bg-slate-800 flex items-center justify-between">
                              <div className="flex flex-col">
                                <span className={`text-sm ${alarmSound === track.id ? 'text-white font-bold' : 'text-slate-400'} truncate`}>{track.label}</span>
                                <span className="text-[10px] text-slate-500">{track.credit}</span>
                              </div>
                              <input
                                type="radio"
                                name="alarm_sound"
                                value={track.id}
                                checked={alarmSound === track.id}
                                readOnly
                                className="text-orange-600 focus:ring-orange-500 bg-slate-900 border-slate-600 ml-2"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-400 mb-2">Telefone / WhatsApp</label>
                  {isOwnProfile ? (
                    <div className="relative">
                      <Smartphone className="absolute left-3 top-3.5 w-5 h-5 text-slate-500" />
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="(11) 99999-9999"
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-10 pr-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="relative">
                        <Smartphone className="absolute left-3 top-3.5 w-5 h-5 text-slate-500" />
                        <div className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-4 py-3 text-white">
                          {phone || 'Não informado'}
                        </div>
                      </div>
                      {phone && (
                        <div className="grid grid-cols-2 gap-3">
                          <a
                            href={`tel:${phone.replace(/\D/g, '')}`}
                            className="flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-xl transition-colors font-bold text-sm"
                          >
                            <Smartphone className="w-4 h-4" /> Ligar
                          </a>
                          <a
                            href={`https://wa.me/55${phone.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl transition-colors font-bold text-sm"
                          >
                            WhatsApp
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Generation Configuration */}
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700">
                  <div className="mb-4">
                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <Rocket className="w-4 h-4 text-emerald-500" /> Perfil de Ganhos
                    </label>
                    <p className="text-xs text-slate-500 mt-1">Defina como o sistema deve calcular a auditoria dos seus turnos. Essa opção afeta novos cálculos, como estimativas e correções.</p>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div 
                      onClick={() => setGeneration('OLD')}
                      className={`p-3 rounded-xl border cursor-pointer transition-all ${generation === 'OLD' ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-700 bg-slate-800 hover:border-slate-500'}`}
                    >
                      <h4 className={`text-sm font-bold mb-1 ${generation === 'OLD' ? 'text-emerald-400' : 'text-slate-300'}`}>Geração Antiga</h4>
                      <p className="text-[10px] text-slate-400 leading-relaxed">Taxa base de R$ 20,00 e taxa premium de R$ 26,00 (Noturno/Fim de semana/Feriados).</p>
                    </div>

                    <div 
                      onClick={() => setGeneration('NEW')}
                      className={`p-3 rounded-xl border cursor-pointer transition-all ${generation === 'NEW' ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-700 bg-slate-800 hover:border-slate-500'}`}
                    >
                      <h4 className={`text-sm font-bold mb-1 ${generation === 'NEW' ? 'text-emerald-400' : 'text-slate-300'}`}>Geração Nova</h4>
                      <p className="text-[10px] text-slate-400 leading-relaxed">Taxa base fixa de R$ 20,00/h (R$ 0,20 por unidade centesimal) em qualquer horário.</p>
                    </div>
                  </div>
                </div>

                {/* Theme Configuration */}
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700">
                  <div className="mb-4">
                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-orange-500" /> Tema do Aplicativo
                    </label>
                    <p className="text-xs text-slate-500 mt-1">Escolha a aparência visual do seu aplicativo Driver Score.</p>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div 
                      onClick={() => onThemeChange?.('dark')}
                      className={`p-3 rounded-xl border cursor-pointer transition-all flex flex-col justify-between ${theme === 'dark' ? 'border-orange-500 bg-orange-500/10' : 'border-slate-700 bg-slate-800 hover:border-slate-500'}`}
                    >
                      <div>
                        <h4 className={`text-sm font-bold mb-1 ${theme === 'dark' ? 'text-orange-400' : 'text-slate-300'} flex items-center gap-1.5`}>
                          <span>🌙</span> Escuro
                        </h4>
                        <p className="text-[10px] text-slate-400 leading-relaxed">Fundo clássico espacial com tons escuros e detalhes em âmbar.</p>
                      </div>
                      {theme === 'dark' && <span className="text-right text-[10px] text-orange-500 font-bold mt-2">✓ Ativo</span>}
                    </div>

                    <div 
                      onClick={() => onThemeChange?.('light')}
                      className={`p-3 rounded-xl border cursor-pointer transition-all flex flex-col justify-between ${theme === 'light' ? 'border-orange-500 bg-orange-500/10' : 'border-slate-700 bg-slate-800 hover:border-slate-500'}`}
                    >
                      <div>
                        <h4 className={`text-sm font-bold mb-1 ${theme === 'light' ? 'text-orange-400' : 'text-slate-300'} flex items-center gap-1.5`}>
                          <span>☀️</span> Claro
                        </h4>
                        <p className="text-[10px] text-slate-400 leading-relaxed">Visual clean com excelente legibilidade e tons claros e suaves.</p>
                      </div>
                      {theme === 'light' && <span className="text-right text-[10px] text-orange-500 font-bold mt-2">✓ Ativo</span>}
                    </div>
                  </div>
                </div>

                {/* Social Media Section */}
                {isOwnProfile && (
                  <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-amber-500" />
                        <label className="text-sm font-medium text-slate-300">Redes Sociais</label>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowSocials(!showSocials)}
                        className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all border flex items-center gap-1.5 ${showSocials ? 'bg-amber-500/20 text-amber-500 border-amber-500/40 shadow-[0_0_10px_rgba(245,158,11,0.2)]' : 'bg-slate-800 text-slate-500 border-slate-700'}`}
                      >
                        {showSocials ? <><Eye className="w-3 h-3" /> Modo Público</> : <><EyeOff className="w-3 h-3" /> Modo Privado</>}
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] text-slate-500 font-bold uppercase mb-1 block">Instagram</label>
                        <div className="relative">
                          <Instagram className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-500" />
                          <input type="text" value={instagramUser} onChange={e => setInstagramUser(e.target.value)} placeholder="seu_usuario" className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:ring-2 focus:ring-amber-500/50 outline-none" />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 font-bold uppercase mb-1 block">YouTube</label>
                        <div className="relative">
                          <Youtube className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-500" />
                          <input type="text" value={youtubeChannel} onChange={e => setYoutubeChannel(e.target.value)} placeholder="@canal" className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:ring-2 focus:ring-amber-500/50 outline-none" />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 font-bold uppercase mb-1 block">TikTok</label>
                        <div className="relative">
                          <Globe className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-500" />
                          <input type="text" value={tiktokUser} onChange={e => setTiktokUser(e.target.value)} placeholder="seu_usuario" className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:ring-2 focus:ring-amber-500/50 outline-none" />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 font-bold uppercase mb-1 block">LinkedIn</label>
                        <div className="relative">
                          <Linkedin className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-500" />
                          <input type="text" value={linkedinUser} onChange={e => setLinkedinUser(e.target.value)} placeholder="perfil" className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:ring-2 focus:ring-amber-500/50 outline-none" />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 font-bold uppercase mb-1 block">X (Twitter)</label>
                        <div className="relative">
                          <Twitter className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-500" />
                          <input type="text" value={xUser} onChange={e => setXUser(e.target.value)} placeholder="seu_usuario" className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:ring-2 focus:ring-amber-500/50 outline-none" />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 font-bold uppercase mb-1 block">Facebook</label>
                        <div className="relative">
                          <Facebook className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-500" />
                          <input type="text" value={facebookUser} onChange={e => setFacebookUser(e.target.value)} placeholder="perfil" className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:ring-2 focus:ring-amber-500/50 outline-none" />
                        </div>
                      </div>
                    </div>

                    {showSocials && (
                      <p className="text-[10px] text-amber-500/60 text-center">✨ Suas redes serão visíveis para a comunidade no seu perfil público.</p>
                    )}
                  </div>
                )}

                {isOwnProfile && (
                  <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700 space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-emerald-500" />
                      <label className="text-sm font-medium text-slate-300">Privacidade do Perfil</label>
                    </div>

                    {/* Hide Stats Toggle */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-800/30">
                      <div className="pr-2">
                        <p className="text-sm text-white font-medium flex items-center gap-2">
                          {privacyHideStats ? <EyeOff className="w-4 h-4 text-slate-500" /> : <Eye className="w-4 h-4 text-blue-400" />}
                          Estatísticas no Ranking
                        </p>
                        <p className="text-[10px] text-slate-400 mt-1 leading-tight">Esconder XP, Elo e progresso de quem vir o seu perfil.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPrivacyHideStats(!privacyHideStats)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacyHideStats ? 'bg-emerald-500' : 'bg-slate-700'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacyHideStats ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>

                    {/* Hide Avatar Toggle */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-800/30">
                      <div className="pr-2">
                        <p className="text-sm text-white font-medium flex items-center gap-2">
                          {privacyHideAvatar ? <Lock className="w-4 h-4 text-slate-500" /> : <Unlock className="w-4 h-4 text-indigo-400" />}
                          Ocultar Foto de Perfil
                        </p>
                        <p className="text-[10px] text-slate-400 mt-1 leading-tight">Não mostrar sua foto no quadro de líderes ou no seu perfil público.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPrivacyHideAvatar(!privacyHideAvatar)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacyHideAvatar ? 'bg-emerald-500' : 'bg-slate-700'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacyHideAvatar ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  </div>
                )}

                {isOwnProfile && (
                  <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700 space-y-4 mt-4 animate-fade-in">
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="w-4 h-4 text-orange-500" />
                      <label className="text-sm font-medium text-slate-300">Preferências de Tours Explicativos</label>
                    </div>

                    {/* Onboarding Tour Toggle */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-800/30">
                      <div className="pr-2">
                        <p className="text-sm text-white font-medium flex items-center gap-2">
                          <Rocket className="w-4 h-4 text-orange-500" />
                          Tour de Boas-Vindas do App
                        </p>
                        <p className="text-[10px] text-slate-400 mt-1 leading-tight">Mostrar automaticamente o tour guiado do painel inicial para novos usuários.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const nextVal = !tourEnabled;
                          setTourEnabled(nextVal);
                          localStorage.setItem('driver_score_tour_enabled', nextVal ? 'true' : 'false');
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${tourEnabled ? 'bg-emerald-500' : 'bg-slate-700'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${tourEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>

                    {/* Garage Tour Toggle */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-800/30">
                      <div className="pr-2">
                        <p className="text-sm text-white font-medium flex items-center gap-2">
                          <Car className="w-4 h-4 text-blue-400" />
                          Tour do Ajuste Real (Garagem)
                        </p>
                        <p className="text-[10px] text-slate-400 mt-1 leading-tight">Mostrar automaticamente as dicas de como funciona o Ajuste Real de turno na Garagem.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const nextVal = !garageTourEnabled;
                          setGarageTourEnabled(nextVal);
                          localStorage.setItem('driver_score_garage_tour_enabled', nextVal ? 'true' : 'false');
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${garageTourEnabled ? 'bg-emerald-500' : 'bg-slate-700'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${garageTourEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  </div>
                )}

                {isOwnProfile && (
                  <div className="space-y-3 mt-4">
                    <button
                      type="submit"
                      disabled={isSaving}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-all shadow-lg"
                    >
                      {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                      Salvar Alterações
                    </button>

                    <button
                      type="button"
                      onClick={onRestartTour}
                      className="w-full bg-slate-700 hover:bg-slate-600 text-slate-300 font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-all border border-slate-600"
                    >
                      <Rocket className="w-5 h-5 text-orange-500" />
                      Reiniciar Tour do App
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        )}

        {/* Right Column: Subscription & Referral Widgets */}
        <div className={`space-y-6 ${!isOwnProfile ? 'lg:col-span-3' : 'lg:col-span-1'}`}>

          {/* Premium Status / Call to Action Box */}
          <div className="bg-slate-850 border border-slate-700 rounded-xl p-5 shadow-lg">
            <h4 className="text-sm font-extrabold text-white mb-4 uppercase tracking-wider flex items-center gap-2">
              <Crown className="w-4 h-4 text-amber-500 animate-bounce" /> Plano Assinatura
            </h4>
            
            {/* Expired Founder Badge (gray) - Visible only to owner or admin */}
            {(isOwnProfile || isAdmin) && isSubscriptionExpired && profileToDisplay?.is_founder && (
              <div className="bg-slate-700/30 border border-slate-600/30 p-4 rounded-xl flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-600/20 flex items-center justify-center">
                      <Star className="text-slate-500 fill-slate-500" size={20} />
                    </div>
                    <div>
                      <h4 className="text-slate-400 font-bold text-sm">Assinatura Fundador Expirada</h4>
                      <p className="text-slate-500 text-[10px]">
                        Expirou em: {new Date(profileToDisplay.subscription_end_date!).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-slate-300 text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-tighter bg-slate-600">
                    Founder
                  </div>
                </div>
                {isOwnProfile && (
                  <button
                    onClick={() => setIsSubscriptionModalOpen(true)}
                    className="w-full py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 active:scale-95 text-slate-900 text-xs font-bold rounded-lg shadow-lg shadow-orange-500/20 transition-all flex items-center justify-center gap-2"
                  >
                    <Crown size={16} />
                    Renovar Assinatura Fundador — R$ 10,90/mês
                  </button>
                )}
              </div>
            )}

            {/* Active Premium */}
            {profileToDisplay?.is_premium && !isSubscriptionExpired ? (
              <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-xl flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <Star className="text-amber-500 fill-amber-500" size={20} />
                    </div>
                    <div>
                      <h4 className="text-amber-500 font-bold text-sm">Assinatura {profileToDisplay.is_founder ? 'Fundador' : 'Elite'} Ativa</h4>
                      <p className="text-slate-400 text-[10px]">
                        {isOwnProfile || isViewingOtherAsAdmin ? (
                          <>Expira em: {profileToDisplay.subscription_end_date ? new Date(profileToDisplay.subscription_end_date).toLocaleDateString() : 'Renovação automática'}</>
                        ) : (
                          'Membro ativo de elite da comunidade'
                        )}
                      </p>
                    </div>
                  </div>
                  <div className={`text-slate-900 text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-tighter ${profileToDisplay.is_founder ? 'bg-gradient-to-r from-amber-300 to-yellow-500 shadow-[0_0_10px_rgba(251,191,36,0.5)]' : 'bg-amber-500'}`}>
                    {profileToDisplay.is_founder ? 'Founder' : 'Elite'}
                  </div>
                </div>
                {isOwnProfile && profileToDisplay.subscription_id && (
                  <button
                    onClick={handleCancelSubscription}
                    disabled={isSaving}
                    className="w-full py-2 bg-slate-800 hover:bg-slate-700 text-red-400 text-xs font-bold rounded-lg border border-red-900/30 transition-colors flex items-center justify-center disabled:opacity-50"
                    title="Cancelar a renovação automática da assinatura mensal"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancelar Assinatura'}
                  </button>
                )}
              </div>
            ) : !isSubscriptionExpired && isOwnProfile && (
              <button
                onClick={() => setIsSubscriptionModalOpen(true)}
                className="w-full bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 active:scale-95 text-slate-900 font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-orange-500/20 group"
              >
                <Crown size={18} className="group-hover:rotate-12 transition-transform" />
                Seja Driver Score Premium
                <Zap size={16} className="fill-current" />
              </button>
            )}

            {/* Upsell for Non-Premium Visitors viewing another profile */}
            {!isOwnProfile && !userProfile?.is_premium && !isVipEmail(userProfile?.email) && (
              <button
                onClick={() => setIsSubscriptionModalOpen(true)}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 active:scale-95 text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-purple-500/20 group animate-pulse-gentle"
              >
                <Crown size={18} className="group-hover:rotate-12 transition-transform text-amber-400" />
                Seja também Premium Ilimitado
                <Zap size={16} className="fill-current text-amber-400" />
              </button>
            )}
          </div>

          {/* Zere Seu Ano - Referral Tracking Section */}
          {isOwnProfile && profileToDisplay?.is_premium && (
            <div className="bg-emerald-950/20 border border-emerald-500/30 p-5 rounded-xl relative overflow-hidden shadow-lg">
               <div className="absolute top-0 right-0 p-2 text-emerald-500/10">
                  <Sparkles size={80} className="-mr-4 -mt-4" />
               </div>
               <div className="relative z-10 flex flex-col gap-3">
                 <div className="flex items-center gap-2">
                   <Sparkles className="text-emerald-400 animate-pulse" size={18} />
                   <h4 className="text-emerald-400 font-bold text-sm">Zere seu Ano! (Indique e Ganhe)</h4>
                 </div>
                 
                 <p className="text-slate-300 text-xs leading-relaxed">
                   Compartilhe seu link exclusivo. A cada amigo que assinar, <strong>vocês dois ganham 3 meses extras</strong> de Driver Score Premium!
                 </p>

                 <div className="flex items-center justify-between bg-slate-900/50 rounded-lg p-2.5 border border-slate-700/50">
                   <span className="text-slate-400 text-[10px] font-bold">SEU CÓDIGO</span>
                   <span className="text-emerald-300 font-black tracking-widest">{profileToDisplay.referral_code || profileToDisplay.id.substring(0,8).toUpperCase()}</span>
                 </div>

                 <div className="space-y-1">
                   <div className="flex justify-between text-[10px] text-slate-400 font-bold">
                     <span>Progresso de Indicações</span>
                     <span className={referralCount >= 4 ? 'text-emerald-400' : ''}>{referralCount} / 4</span>
                   </div>
                   <div className="w-full bg-slate-900 rounded-full h-2 border border-slate-800">
                     <div className="bg-gradient-to-r from-emerald-500 to-emerald-400 h-full rounded-full transition-all duration-1000" style={{ width: `${Math.min(100, (referralCount / 4) * 100)}%` }}></div>
                   </div>
                 </div>
               </div>
            </div>
          )}

          {/* Referral Stats (Elite Only) */}
          {isOwnProfile && isPremiumOrVip && (
            <div className="bg-gradient-to-br from-indigo-950/40 to-slate-900 border border-indigo-500/25 p-5 rounded-xl shadow-lg relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                  <UserPlus size={80} className="text-indigo-400 -rotate-12" />
              </div>
              
              <div className="relative z-10">
                <h4 className="text-indigo-300 font-bold text-sm uppercase tracking-widest flex items-center gap-2 mb-4">
                  <Star size={16} className="fill-indigo-400 text-indigo-400" /> Indicações de Sucesso
                </h4>
                
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-slate-900/50 rounded-xl p-3 border border-slate-700/30">
                    <p className="text-[10px] text-slate-500 uppercase font-bold mb-1">Amigos Elite</p>
                    <p className="text-xl font-black text-white">{referralCount}</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-xl p-3 border border-slate-700/30">
                    <p className="text-[10px] text-slate-500 uppercase font-bold mb-1">Bônus Acumulado</p>
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-black text-emerald-400">{referralCount * 3}</span>
                      <span className="text-[10px] font-normal text-emerald-500/70">meses</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <p className="text-xs text-indigo-200/50 leading-tight">
                    Convide seus amigos! Se eles assinarem o Modo Elite em até 30 dias, **vocês dois ganham 3 meses grátis** na hora.
                  </p>
                  <div className="pt-1">
                    <ShareApp />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rules Info Table */}
          <div className="bg-slate-800/60 p-5 rounded-xl border border-slate-700 shadow-lg">
            <h4 className="text-sm font-bold text-slate-400 mb-3.5 uppercase tracking-wider flex items-center gap-2">
              <Trophy className="w-4 h-4 text-yellow-500" /> Tabela de XP
            </h4>
            <ul className="space-y-2 text-xs text-slate-400">
              <li className="flex justify-between border-b border-slate-800 pb-1.5"><span>Turno (Trabalho)</span> <span className="text-green-400 font-bold">+20 XP</span></li>
              <li className="flex justify-between border-b border-slate-800 pb-1.5"><span>Turno com Ganhos Extras</span> <span className="text-green-400 font-bold">+10 XP</span></li>
              <li className="flex justify-between border-b border-slate-800 pb-1.5"><span>Registro de Gasto</span> <span className="text-green-400 font-bold">+5 XP</span></li>
              <li className="flex justify-between border-b border-slate-800 pb-1.5"><span>Registro de Folga</span> <span className="text-slate-300 font-bold">+10 XP</span></li>
              <li className="flex justify-between pt-1"><span>Falta (No Show)</span> <span className="text-red-400 font-bold">-100 XP</span></li>
            </ul>
          </div>

          {/* Sair da Conta */}
          {!viewingProfileId && (
            <div className="pt-2">
              <button
                type="button"
                onClick={logoutUser}
                className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-red-950/40 hover:bg-red-900/60 active:scale-98 text-red-300 hover:text-red-100 border border-red-500/30 rounded-xl font-bold text-sm transition-all shadow-lg cursor-pointer"
              >
                <LogOut className="w-4 h-4" /> Deslogar / Sair da Conta
              </button>
            </div>
          )}

        </div>
      </div>

      {/* Lazy Loaded YouTube Modal */}
      {
        playingYtId && (
          <div className="fixed inset-0 z-[70] bg-black/95 flex flex-col items-center justify-center p-4 animate-fade-in backdrop-blur-md" onClick={stopAlarm}>
            <div className="relative w-full max-w-4xl aspect-video bg-black rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.8)] border border-slate-800" onClick={e => e.stopPropagation()}>
              <button onClick={stopAlarm} className="absolute z-10 top-4 right-4 bg-black/50 hover:bg-red-600 text-white p-2 rounded-full transition-colors backdrop-blur-md">
                <X className="w-6 h-6" />
              </button>
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${playingYtId}?autoplay=1&rel=0`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <p className="text-slate-400 mt-6 text-sm flex items-center gap-2">
              <Play className="w-4 h-4" /> Tocando diretamente do YouTube
            </p>
          </div>
        )
      }

      <SubscriptionModal
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
        user={userProfile}
        onSuccess={() => {
          setIsSubscriptionModalOpen(false);
          onProfileUpdate();
        }}
      />
    </div>
  );
};

